import { useState, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { AdminSubscriptionItem } from "@/types/subscription";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import {
  getSubscriptionColumns,
  type SubscriptionAction,
} from "./subscription-columns";
import { SubscriptionCardList } from "./subscription-card-list";
import { SubscriptionPagination } from "./subscription-pagination";
import { RefundDialog } from "./refund-dialog";
import { UpgradeDialog } from "./upgrade-dialog";
import { ExtendDialog } from "./extend-dialog";
import { GrantCreditsDialog } from "./grant-credits-dialog";

interface SubscriptionTableProps {
  data: AdminSubscriptionItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

export function SubscriptionTable({
  data,
  meta,
  pagination,
}: SubscriptionTableProps) {
  const [selectedSubscription, setSelectedSubscription] =
    useState<AdminSubscriptionItem | null>(null);
  const [dialogType, setDialogType] = useState<SubscriptionAction | null>(
    null
  );

  const handleAction = useCallback(
    (subscription: AdminSubscriptionItem, action: SubscriptionAction) => {
      setSelectedSubscription(subscription);
      setDialogType(action);
    },
    []
  );

  const handleDialogClose = useCallback(() => {
    setDialogType(null);
    setSelectedSubscription(null);
  }, []);

  const columns = useMemo(
    () => getSubscriptionColumns(handleAction),
    [handleAction]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">
          검색 조건에 맞는 구독이 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-md border overflow-auto max-h-[calc(100vh-280px)]">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="hover:bg-muted/50 has-[[data-state=open]]:bg-muted transition-colors duration-75"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden">
        <SubscriptionCardList data={data} onAction={handleAction} />
      </div>

      <SubscriptionPagination meta={meta} pagination={pagination} />

      {selectedSubscription && (
        <>
          <RefundDialog
            subscription={selectedSubscription}
            open={dialogType === "refund"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
          <UpgradeDialog
            subscription={selectedSubscription}
            open={dialogType === "upgrade"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
          <ExtendDialog
            subscription={selectedSubscription}
            open={dialogType === "extend"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
          <GrantCreditsDialog
            subscription={selectedSubscription}
            open={dialogType === "grantCredits"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
        </>
      )}
    </div>
  );
}
